import { Alert, Button, Space, Spin, Typography, Upload, message } from 'antd';
import {
	useFinalizeInternalOrderMutation,
	useGetInternalOrderLineItemsQuery,
	useReanalyzeInternalFileMutation,
	useUploadInternalFilesMutation,
} from '../../redux/api/order';
import type { AnalysisLineItem } from './AddInternalOrder/AnalysisStep';
import {
	AnalysisStatusTag,
	analyzedCount,
	finalizeEnabled,
} from './AddInternalOrder/AnalysisStep';
import { notReadyMessage } from './AddInternalOrder';
import { useGetLoggedInUserQuery } from '../../redux/api/users';
import { isInternalOrdersManager } from '../../utils/isInternalOrdersManager';

type InternalFileField = 'internal_step_file' | 'internal_drawing_file';

const STEP_FILE_RE = /\.(step|stp)$/i;
const DRAWING_FILE_RE = /\.(pdf|png|jpe?g)$/i;

/**
 * Resolves which multipart field a dropped file belongs to, or `null` when
 * the file cannot legally fill any missing slot. The extension gates the
 * slot (mirroring the wizard's Step 1 `accept` props: `.step`/`.stp` for the
 * 3D file, `.pdf`/`.png`/`.jpg` for the drawing) — a drawing must never be
 * stored as `internal_step_file` or vice versa, since a misfiled upload
 * breaks analysis and strands the draft. When both slots are missing the
 * extension decides which one the file fills.
 */
export const resolveUploadField = (
	file: { name: string },
	missingStep: boolean,
	missingDrawing: boolean,
): InternalFileField | null => {
	const isStep = STEP_FILE_RE.test(file.name);
	const isDrawing = DRAWING_FILE_RE.test(file.name);
	if (!isStep && !isDrawing) return null;
	if (missingStep && !missingDrawing) return isStep ? 'internal_step_file' : null;
	if (missingDrawing && !missingStep) return isDrawing ? 'internal_drawing_file' : null;
	return isStep ? 'internal_step_file' : 'internal_drawing_file';
};

/**
 * Classifies every file from a single Upload interaction (one or two files —
 * AntD hands `beforeUpload` the whole batch's `fileList`) into the field(s)
 * it belongs to; files whose extension doesn't fit any missing slot land in
 * `rejected` instead of being misfiled. Used to build exactly one
 * `FormData`/one mutation call per interaction — see the caller for why that
 * "exactly one" matters.
 */
export const classifyFiles = <T extends { name: string }>(
	fileList: readonly T[],
	missingStep: boolean,
	missingDrawing: boolean,
): { assigned: Partial<Record<InternalFileField, T>>; rejected: T[] } => {
	const assigned: Partial<Record<InternalFileField, T>> = {};
	const rejected: T[] = [];
	fileList.forEach((file) => {
		const field = resolveUploadField(file, missingStep, missingDrawing);
		if (field === null || assigned[field] !== undefined) {
			// Unsupported extension, wrong type for the sole missing slot, or a
			// second file resolving to an already-claimed slot — overwriting
			// would silently discard the first file of the pair.
			rejected.push(file);
		} else {
			assigned[field] = file;
		}
	});
	return { assigned, rejected };
};

export interface DraftBannerBodyProps {
	lineItems: AnalysisLineItem[];
	onRetry: (id: string) => void;
	/**
	 * Fired once per Upload interaction with every selected file already
	 * classified — never per-file. Batched as one call per interaction (not
	 * one per file) because that's what the user actually did: a single
	 * "select both files" action should be one request with one
	 * success-or-failure outcome, not two independent ones the UI would have
	 * to track and reconcile separately. `upload-internal-files/` now scopes
	 * its save to exactly the fields in the request (`update_fields`, added
	 * in the internal-orders generic-endpoint hardening follow-up), so two
	 * concurrent single-field PATCHes from the same row no longer race each
	 * other's field back to NULL — batching stays correct regardless, for the
	 * UX reason above.
	 */
	onUpload: (id: string, files: Partial<Record<InternalFileField, File>>) => void;
	onFinalize: () => void;
	isFinalizing?: boolean;
	/** Row id whose upload PATCH is currently in flight, if any — disables
	 * that row's Upload control so a double-click can't fire an overlapping
	 * request for the same row. */
	uploadingId?: string | null;
	/**
	 * Whether the current user may mutate this draft (Shop Manager group, or
	 * superuser). Defaults to `false` (fail closed): a caller that forgets to
	 * pass it gets the safe read-only view rather than accidentally exposing
	 * mutation controls. The wired `InternalDraftBanner` below always passes
	 * the real value explicitly, once it's known (see its own loading gate).
	 * `false` renders a read-only view -- status chips and the analyzed/total
	 * progress stay visible, but Finalize, Retry, and Upload controls are
	 * hidden, mirroring the backend's InInternalOrdersManageGroup guard so
	 * the UI never offers a control the API would 403 on.
	 */
	canManage?: boolean;
}

/**
 * Pure draft-resume banner body. Given the order-scoped line items (the same
 * shape the wizard's Step 2 polls via `useGetInternalOrderLineItemsQuery`)
 * and callbacks, renders the "N/M analyzed" progress header, per-row analysis
 * status with Retry for failures, an Upload control for rows still missing a
 * file, and a Finalize button gated exactly like the wizard (`finalizeEnabled`).
 */
export const DraftBannerBody = ({
	lineItems,
	onRetry,
	onUpload,
	onFinalize,
	isFinalizing,
	uploadingId,
	canManage = false,
}: DraftBannerBodyProps) => {
	const analyzed = analyzedCount(lineItems);

	return (
		<Alert
			type="warning"
			showIcon
			className="mb-4"
			message={
				<Space style={{ display: 'flex', justifyContent: 'space-between' }}>
					<strong>
						Draft — {analyzed}/{lineItems.length} line items analyzed
					</strong>
					{canManage && (
						<Button
							type="primary"
							loading={isFinalizing}
							disabled={!finalizeEnabled(lineItems)}
							onClick={onFinalize}
						>
							Finalize
						</Button>
					)}
				</Space>
			}
			description={
				<Space direction="vertical" style={{ display: 'flex' }}>
					<span className="text-gray-500">
						{canManage
							? 'This internal order hasn\'t been finalized yet. Resume it below — retry failed analyses, upload any missing files, then finalize.'
							: 'This internal order hasn\'t been finalized yet.'}
					</span>
					{lineItems.map((li) => {
						const missingStep = !li.internal_step_file;
						const missingDrawing = !li.internal_drawing_file;
						const needsUpload = missingStep || missingDrawing;
						const isUploadingRow = li.id === uploadingId;
						return (
							<Space
								key={li.id}
								style={{ display: 'flex', justifyContent: 'space-between' }}
							>
								<span>{li.name}</span>
								<Space>
									<AnalysisStatusTag
										status={li.internal_file_analysis_status}
									/>
									{li.internal_file_analysis_status === 'failed' &&
										li.internal_analysis_error && (
											<Typography.Text type="danger">
												{li.internal_analysis_error}
											</Typography.Text>
										)}
									{canManage && li.internal_file_analysis_status === 'failed' && (
										<Button danger size="small" onClick={() => onRetry(li.id)}>
											Retry
										</Button>
									)}
									{canManage && needsUpload && (
										<Upload
											multiple={missingStep && missingDrawing}
											showUploadList={false}
											disabled={isUploadingRow}
											accept=".step,.stp,.pdf,.png,.jpg"
											beforeUpload={(file, fileList) => {
												// AntD calls beforeUpload once per file in a multi-select
												// batch, but `fileList` is always the *whole* batch — wait
												// for the last invocation, then fire ONE onUpload call
												// covering every file selected in this interaction.
												if (file !== fileList[fileList.length - 1]) return false;
												const { assigned, rejected } = classifyFiles(
													fileList,
													missingStep,
													missingDrawing,
												);
												if (rejected.length > 0) {
													message.error(
														`${rejected.map((f) => f.name).join(', ')}: wrong file type — the 3D model must be .step/.stp and the drawing .pdf/.png/.jpg.`,
													);
												}
												if (Object.keys(assigned).length > 0) {
													onUpload(li.id, assigned);
												}
												return false;
											}}
										>
											<Button
												size="small"
												loading={isUploadingRow}
												disabled={isUploadingRow}
											>
												Upload files
											</Button>
										</Upload>
									)}
								</Space>
							</Space>
						);
					})}
				</Space>
			}
		/>
	);
};

/**
 * Wired banner: polls the order-scoped line items (drafts included) the same
 * way the wizard's Step 2 does, and hooks Retry/Upload/Finalize to the real
 * mutations. Finalize doesn't need an explicit refetch — `order.ts` wires
 * `finalizeInternalOrder` to invalidate the `{type: 'Order', id}` tag that
 * `getOrderById` now provides, so the order-detail page's own query refetches
 * on its own and this banner naturally stops rendering once `is_draft` flips.
 */
const InternalDraftBanner = ({ orderId }: { orderId: string }) => {
	const { data: loggedInUser, isLoading: isLoadingUser } = useGetLoggedInUserQuery();
	const canManage = isInternalOrdersManager(loggedInUser);
	const { data, isLoading } = useGetInternalOrderLineItemsQuery(orderId, {
		pollingInterval: 3000,
	});
	const [
		uploadInternalFiles,
		{ isLoading: isUploading, originalArgs: uploadArgs },
	] = useUploadInternalFilesMutation();
	const [reanalyzeInternalFile] = useReanalyzeInternalFileMutation();
	const [finalizeInternalOrder, { isLoading: isFinalizing }] =
		useFinalizeInternalOrderMutation();

	const lineItems = data?.results ?? [];
	// Derived straight from the mutation hook rather than tracked in separate
	// state: `originalArgs` is the {id, formData} of the last-triggered PATCH,
	// so while it's in flight this is exactly the row that owns it.
	const uploadingId = isUploading ? (uploadArgs?.id ?? null) : null;

	const onRetry = async (id: string) => {
		try {
			await reanalyzeInternalFile(id).unwrap();
			message.success('Analysis restarted');
		} catch (error) {
			console.error('Failed to restart analysis:', error);
			message.error('Failed to restart analysis');
		}
	};

	// One call per Upload interaction, one FormData carrying every file the
	// user selected in that interaction — never one PATCH per file: that
	// matches the single user action driving it and avoids the UI having to
	// track/reconcile two independent in-flight requests for one row. See
	// `DraftBannerBodyProps.onUpload` above for the full rationale.
	const onUpload = async (
		id: string,
		files: Partial<Record<InternalFileField, File>>,
	) => {
		const formData = new FormData();
		(Object.entries(files) as [InternalFileField, File | undefined][]).forEach(
			([field, file]) => {
				if (file) formData.append(field, file, file.name);
			},
		);
		try {
			await uploadInternalFiles({ id, formData }).unwrap();
			message.success('File uploaded');
		} catch (error) {
			console.error('Failed to upload file:', error);
			message.error('Failed to upload the file. Please try again.');
		}
	};

	const onFinalize = async () => {
		try {
			await finalizeInternalOrder(orderId).unwrap();
			message.success('Internal order finalized');
		} catch (error) {
			console.error('Failed to finalize internal order:', error);
			message.error(notReadyMessage(error) ?? 'Failed to finalize the order');
		}
	};

	// Gate on BOTH queries. On a cache-miss first render (hard refresh / deep
	// link) the line-items query can resolve before the logged-in-user query
	// does. If this only checked `isLoading` (line items), a real Shop
	// Manager would see the banner render read-only for a moment --
	// `canManage` defaults to false while `loggedInUser` is still undefined
	// -- with Finalize/Retry/Upload popping in once the user query resolves.
	// Mirrors AddInternalOrder/index.tsx's `isLoadingUser` gate.
	if (isLoading || isLoadingUser) return <Spin />;

	return (
		<DraftBannerBody
			lineItems={lineItems}
			onRetry={onRetry}
			onUpload={onUpload}
			onFinalize={onFinalize}
			isFinalizing={isFinalizing}
			uploadingId={uploadingId}
			canManage={canManage}
		/>
	);
};

export default InternalDraftBanner;
